import { utils, write } from 'xlsx';
import { MIME_XLSX } from '../../utils/mimeTypes.ts';
import { parsePositionNumber } from '../cmm-import/parsePositionNumber.ts';
import { readZeissCmmFile } from './readZeissCmmFile.ts';
import type { ZeissFileReadResult, ZeissPositionRead } from './readZeissCmmFile.ts';

export interface CombineCmmSummary {
    fileCount: number;
    successCount: number;
    failedCount: number;
    partCount: number;
    positionCount: number;
    expectedPositionCount: number | null;
    unexpectedPositionFiles: number;
}

export interface CombineCmmFailure {
    fileName: string;
    errorMessage: string;
}

export interface CombineCmmResult {
    blob: Blob;
    summary: CombineCmmSummary;
    failures: CombineCmmFailure[];
    fileDetails: CombineCmmFileDetail[];
    warnings: CombineCmmWarning[];
}

export interface CombineCmmFileDetail {
    fileName: string;
    isIncluded: boolean;
    partCount: number;
    includedPositions: number;
    positionNames: string[];
    skippedPositions: number;
    skippedPositionNames: string[];
    skippedReason: string;
    warningMessages: string[];
}

export interface CombineCmmWarning {
    fileName?: string;
    message: string;
}

export interface CombineCmmAnalysis {
    summary: CombineCmmSummary;
    failures: CombineCmmFailure[];
    fileDetails: CombineCmmFileDetail[];
    warnings: CombineCmmWarning[];
}

export interface CombineCmmOptions {
    expectedPositionsPerFile?: number | null;
    expectedPartsPerFile?: number | null;
}

interface CombineCmmReadResult {
    file: File;
    readResult: ZeissFileReadResult;
}

export async function analyzeCmmFiles(files: File[], options: CombineCmmOptions = {}): Promise<CombineCmmAnalysis> {
    const readResults = await readCmmFiles(files);
    return createCombineCmmAnalysis(readResults, options);
}

export async function combineCmmFilesToXlsx(files: File[], options: CombineCmmOptions = {}): Promise<CombineCmmResult> {
    const readResults = await readCmmFiles(files);
    const analysis = createCombineCmmAnalysis(readResults, options);

    const successful = readResults.filter((it) => it.readResult.success);
    if (successful.length === 0) {
        throw new Error('None of the selected files could be parsed as a Zeiss Calypso CMM report.');
    }

    const combinedRows: (string | number)[][] = [
        ['Part No.', 'Position name', 'Actual', 'Nominal', 'Upper Tol', 'Lower Tol', 'Deviation', 'Source file', 'Date', 'Time'],
    ];

    for (const { file, readResult } of successful) {
        if (!readResult.success) continue;
        const { metadata, positions } = readResult;
        const groups = splitIntoPartGroups(positions);

        for (const partPositions of groups) {
            for (const position of partPositions) {
                combinedRows.push([
                    metadata.partNo,
                    position.positionName,
                    position.actual,
                    position.nominal,
                    position.upperTol,
                    position.lowerTol,
                    position.deviation,
                    file.name,
                    metadata.date,
                    metadata.time,
                ]);
            }
        }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, utils.aoa_to_sheet(combinedRows), 'Combined');

    const excelBuffer = write(workbook, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([excelBuffer], { type: MIME_XLSX });

    return {
        blob,
        ...analysis,
    };
}

async function readCmmFiles(files: File[]): Promise<CombineCmmReadResult[]> {
    const readResults = await Promise.all(
        files.map(async (file) => ({
            file,
            readResult: await readZeissCmmFile(file),
        })),
    );

    return readResults;
}

export function createCombineCmmAnalysis(readResults: CombineCmmReadResult[], options: CombineCmmOptions = {}): CombineCmmAnalysis {
    const expectedPositionsPerFile = normalizeExpectedPositionsPerFile(options.expectedPositionsPerFile ?? options.expectedPartsPerFile);
    const failures: CombineCmmFailure[] = readResults
        .filter((it) => !it.readResult.success)
        .map((it) => ({
            fileName: it.file.name,
            errorMessage: !it.readResult.success ? it.readResult.errorMessage : 'Unknown read error',
        }));

    const fileDetails: CombineCmmFileDetail[] = readResults.map(({ file, readResult }) => {
        if (!readResult.success) {
            return {
                fileName: file.name,
                isIncluded: false,
                partCount: 0,
                includedPositions: 0,
                positionNames: [],
                skippedPositions: 0,
                skippedPositionNames: [],
                skippedReason: readResult.errorMessage,
                warningMessages: [readResult.errorMessage],
            };
        }

        const partCount = splitIntoPartGroups(readResult.positions).length;
        const warningMessages: string[] = [];
        if (expectedPositionsPerFile !== null && readResult.positions.length !== expectedPositionsPerFile) {
            warningMessages.push(`Found ${readResult.positions.length} valid positions, expected ${expectedPositionsPerFile}.`);
        }
        if (readResult.skippedInvalidPositions > 0) {
            warningMessages.push(
                `Skipped ${readResult.skippedInvalidPositions} rows with invalid position numbers ${formatSkippedNames(readResult.skippedInvalidPositionNames)}.`
            );
        }

        return {
            fileName: file.name,
            isIncluded: true,
            partCount,
            includedPositions: readResult.positions.length,
            positionNames: readResult.positions.map((position) => position.positionName),
            skippedPositions: readResult.skippedInvalidPositions,
            skippedPositionNames: readResult.skippedInvalidPositionNames,
            skippedReason: readResult.skippedInvalidPositions > 0 ? 'Skipped invalid position number rows.' : '',
            warningMessages,
        };
    });

    const successful = readResults.filter((it) => it.readResult.success);
    const partCount = fileDetails.reduce((sum, detail) => sum + detail.partCount, 0);
    const positionCount = fileDetails.reduce((sum, detail) => sum + detail.includedPositions, 0);
    const expectedPositionCount = expectedPositionsPerFile === null ? null : readResults.length * expectedPositionsPerFile;
    const unexpectedPositionFiles = expectedPositionsPerFile === null
        ? 0
        : fileDetails.filter((detail) => detail.isIncluded && detail.includedPositions !== expectedPositionsPerFile).length;
    const warnings = createWarnings(fileDetails, failures, positionCount, expectedPositionCount);

    return {
        failures,
        fileDetails,
        warnings,
        summary: {
            fileCount: readResults.length,
            successCount: successful.length,
            failedCount: failures.length,
            partCount,
            positionCount,
            expectedPositionCount,
            unexpectedPositionFiles,
        },
    };
}

export function splitIntoPartGroups(positions: ZeissPositionRead[]): ZeissPositionRead[][] {
    if (positions.length === 0) return [];

    const groups: ZeissPositionRead[][] = [];
    let currentGroup: ZeissPositionRead[] = [];
    const seen = new Set<string>();

    for (const pos of positions) {
        const positionKey = parsePositionNumber(pos.positionName) ?? pos.positionName;
        if (seen.has(positionKey)) {
            groups.push(currentGroup);
            currentGroup = [pos];
            seen.clear();
            seen.add(positionKey);
        } else {
            currentGroup.push(pos);
            seen.add(positionKey);
        }
    }
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    return groups;
}

function normalizeExpectedPositionsPerFile(value: number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.floor(value);
}

function createWarnings(
    fileDetails: CombineCmmFileDetail[],
    failures: CombineCmmFailure[],
    positionCount: number,
    expectedPositionCount: number | null
): CombineCmmWarning[] {
    const warnings: CombineCmmWarning[] = [];

    if (expectedPositionCount !== null && positionCount !== expectedPositionCount) {
        warnings.push({
            message: `Expected ${expectedPositionCount} valid positions from the selected files, but detected ${positionCount}.`,
        });
    }

    for (const failure of failures) {
        warnings.push({
            fileName: failure.fileName,
            message: 'File could not be parsed and will not be included.',
        });
    }

    for (const detail of fileDetails.filter((it) => it.isIncluded)) {
        for (const message of detail.warningMessages) {
            warnings.push({
                fileName: detail.fileName,
                message,
            });
        }
    }

    return warnings;
}

function formatSkippedNames(names: string[]): string {
    if (names.length === 0) return '';

    const visibleNames = names.slice(0, 5);
    const hiddenCount = names.length - visibleNames.length;
    return hiddenCount > 0
        ? `(${visibleNames.join(', ')} and ${hiddenCount} more)`
        : `(${visibleNames.join(', ')})`;
}
